import { Box, Button, Flex, Input, Link, Text, Tooltip, useToast } from '@chakra-ui/react';
import {
  useCreateQaLabelBatch,
  useDeleteQaLabels,
  useToggleQaLabelResultStatus,
  useUndoQaLabelBatch,
  useUpdateQaLabelNotes,
  invalidateChipQaQueries,
} from '@resistapp/client/hooks/api';
import type { QaLabelBatchResponse } from '@resistapp/common/api-types';
import {
  buildQaLabelScopeEntryKey,
  doesQaLabelTypeAllowResultApplication,
  getQaInvalidResultApplicationMessage,
  isQaLabelTmInRange,
  QA_LABEL_TM_MAX,
  QA_LABEL_TM_MIN,
  qaLabelDefinitions,
  qaLabelToScopeEntry,
  qaLabelTypeAllowsTm,
  qaLabelTypeOrder,
} from '@resistapp/common/qa-label-utils';
import { ChipDebugResponse, QaLabel, QaLabelScope, QaLabelScopeEntry, QaLabelType } from '@resistapp/common/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { buildQaLabelChipUrl } from './chip-path-utils';
import {
  appendQaLabelTmShortcutDigit,
  buildQaLabelConcreteRepCoverage,
  buildQaLabelRestoreBatches,
  buildQaLabelResultStatusApplyPlan,
  buildQaLabelTmRewriteBatches,
  getQaLabelShortcutDigit,
  getQaLabelTmInputSelectionRange,
  getQaLabelTmShortcutDigit,
  type QaLabelRestoreBatch,
} from './qa-label-box-utils';
import {
  buildQaSelectionState,
  getDefaultQaScope,
  isImplicitWholeChipQaLabelAllowed,
} from './qa-label-selection-utils';
import { useChipContext } from './use-chip-context';

const qaScopeLabels: Record<QaLabelScope, string> = {
  [QaLabelScope.CHIP]: 'Chip',
  [QaLabelScope.TECHREP]: 'Tech Rep',
  [QaLabelScope.TRIPLICATE]: 'Triplicate',
  [QaLabelScope.SAMPLE]: 'Sample',
  [QaLabelScope.ASSAY]: 'Assay',
};

const standardQaNotesUrl =
  'https://docs.google.com/document/d/1keWv9XpITckrQGo68uJ5no60M7n0YNlWj5bitkgnzQw/edit?tab=t.0#heading=h.8ym9ceq0b6fz';

type QaLabelUndoAction =
  | {
      type: 'created-batches';
      batchIds: string[];
      deletedLabelsToRestore: QaLabel[];
    }
  | {
      type: 'deleted-labels';
      labels: QaLabel[];
    };

type QaLabelKeyboardShortcutState = {
  applyLabel: (labelType: QaLabelType, appliesToResults: boolean) => Promise<void>;
  applyTmLabel: (labelType: QaLabelType, appliesToResults: boolean) => Promise<void>;
  handlePendingTmDigit: (digit: string) => void;
  matchingLabelIdsByType: Record<QaLabelType, number[]>;
  queueQaLabelMutation: (labelType: QaLabelType, task: () => Promise<void>) => Promise<void>;
  readOnlyCoverageByType: Record<QaLabelType, number>;
};

type PendingTmEntryState = {
  labelType: QaLabelType;
  replaceDraft: boolean;
};

export function QaLabelBox() {
  const { chipInfo, debugData, qaLabels, selectedSamples, selectedGroups } = useChipContext();
  const toast = useToast();
  const queryClient = useQueryClient();
  const createBatch = useCreateQaLabelBatch();
  const deleteLabels = useDeleteQaLabels();
  const toggleResultStatus = useToggleQaLabelResultStatus();
  const updateNotes = useUpdateQaLabelNotes();
  const undoBatch = useUndoQaLabelBatch();
  const [noteByLabelType, setNoteByLabelType] = useState<Record<QaLabelType, string>>(createQaLabelNoteMap());
  const [isNoteDirtyByLabelType, setIsNoteDirtyByLabelType] =
    useState<Record<QaLabelType, boolean>>(createQaLabelDirtyMap());
  const [tmByLabelType, setTmByLabelType] = useState<Partial<Record<QaLabelType, string>>>({});
  const [isTmDirtyByLabelType, setIsTmDirtyByLabelType] =
    useState<Record<QaLabelType, boolean>>(createQaLabelDirtyMap());
  const [lastUndoAction, setLastUndoAction] = useState<QaLabelUndoAction | null>(null);
  const [activeMutatingLabelType, setActiveMutatingLabelType] = useState<QaLabelType | null>(null);
  const [activeNoteSavingLabelType, setActiveNoteSavingLabelType] = useState<QaLabelType | null>(null);
  const [activeTmSavingLabelType, setActiveTmSavingLabelType] = useState<QaLabelType | null>(null);
  const [pendingTmFocusLabelType, setPendingTmFocusLabelType] = useState<QaLabelType | null>(null);
  const [isRestoringDeletedLabels, setIsRestoringDeletedLabels] = useState(false);
  const tmInputRefs = useRef<Partial<Record<QaLabelType, HTMLInputElement | null>>>({});
  const tmByLabelTypeRef = useRef<Partial<Record<QaLabelType, string>>>({});
  const tmAppliesToResultsByLabelTypeRef = useRef<Partial<Record<QaLabelType, boolean>>>({});
  const queuedQaMutationRef = useRef<Promise<void>>(Promise.resolve());
  const pendingMatchingLabelIdsByTypeRef = useRef<Record<QaLabelType, number[]>>(createQaLabelIdMap());
  const pendingTmEntryRef = useRef<PendingTmEntryState | null>(null);
  const keyboardShortcutStateRef = useRef<QaLabelKeyboardShortcutState | null>(null);

  const selectionState = useMemo(
    () => buildQaSelectionState(debugData, selectedSamples, selectedGroups),
    [debugData, selectedSamples, selectedGroups],
  );

  const scopeByLabelType = useMemo(
    () =>
      qaLabelTypeOrder.reduce<Record<QaLabelType, QaLabelScope | null>>(
        (acc, labelType) => ({
          ...acc,
          [labelType]: getDefaultQaScope(selectionState.availableScopes, selectedSamples, selectedGroups, labelType),
        }),
        createQaLabelScopeMap(),
      ),
    [selectedGroups, selectedSamples, selectionState.availableScopes],
  );
  const entriesByLabelType = useMemo(
    () =>
      qaLabelTypeOrder.reduce<Record<QaLabelType, QaLabelScopeEntry[]>>((acc, labelType) => {
        const scope = scopeByLabelType[labelType];
        return {
          ...acc,
          [labelType]: scope == null ? [] : selectionState.entriesByScope[scope],
        };
      }, createQaLabelEntryMap()),
    [scopeByLabelType, selectionState.entriesByScope],
  );
  const selectionContextKey = useMemo(
    () =>
      qaLabelTypeOrder
        .map(labelType => {
          const scope = scopeByLabelType[labelType];
          const entries = entriesByLabelType[labelType];
          return scope == null
            ? `${labelType}:null`
            : [labelType, scope, ...entries.map(entry => buildQaLabelScopeEntryKey(scope, entry))].join('::');
        })
        .join('|||'),
    [entriesByLabelType, scopeByLabelType],
  );

  const {
    labelCoverageByType,
    readOnlyCoverageByType,
    readOnlyResultCountByType,
    matchingLabelIdsByType,
    matchingResultLabelCountByType,
    hydratedNoteByLabelType,
    hydratedTmByLabelType,
  } = useMemo(() => {
    const emptyCoverage = createQaLabelCountMap();
    const emptyLabelIds = createQaLabelIdMap();
    const emptyResultCounts = createQaLabelCountMap();
    const emptyHydratedNotes = createQaLabelNoteMap();
    const emptyHydratedTms = createQaLabelTmMap();
    if (selectionState.selectedConcreteReps.length === 0) {
      return {
        labelCoverageByType: emptyCoverage,
        readOnlyCoverageByType: emptyCoverage,
        readOnlyResultCountByType: emptyResultCounts,
        matchingLabelIdsByType: emptyLabelIds,
        matchingResultLabelCountByType: emptyResultCounts,
        hydratedNoteByLabelType: emptyHydratedNotes,
        hydratedTmByLabelType: emptyHydratedTms,
      };
    }

    const labelIdsByType = createQaLabelIdMap();
    const matchingNotesByType = createQaLabelNoteSetMap();
    const matchingTmTextsByType = createQaLabelTmSetMap();
    const focusedWellCoverage = buildQaLabelConcreteRepCoverage(qaLabels, selectionState.selectedConcreteReps);
    const coveredEntryKeysByType = createQaLabelEntryKeyMap();
    const resultLabelCountByType = createQaLabelCountMap();
    const nextReadOnlyCoverageByType = createQaLabelCountMap();
    const readOnlyResultCountsByType = createQaLabelCountMap();

    const coverageByType = createQaLabelCountMap();
    const nextHydratedNoteByLabelType = createQaLabelNoteMap();
    const nextHydratedTmByLabelType = createQaLabelTmMap();
    qaLabelTypeOrder.forEach(labelType => {
      const resolvedScope = scopeByLabelType[labelType];
      const currentEntries = entriesByLabelType[labelType];
      const hasWritableScope =
        resolvedScope != null &&
        currentEntries.length > 0 &&
        (!selectionState.isAllChipImplicit || isImplicitWholeChipQaLabelAllowed(labelType));

      if (resolvedScope != null && currentEntries.length > 0) {
        const selectedEntryKeys = new Set(currentEntries.map(entry => buildQaLabelScopeEntryKey(resolvedScope, entry)));
        qaLabels.forEach(label => {
          if (label.labelType !== labelType || label.scope !== resolvedScope) {
            return;
          }

          const entryKey = buildQaLabelScopeEntryKey(resolvedScope, qaLabelToScopeEntry(label));
          if (!selectedEntryKeys.has(entryKey)) {
            return;
          }

          coveredEntryKeysByType[labelType].add(entryKey);
          labelIdsByType[labelType].push(label.id);
          matchingNotesByType[labelType].add(label.note.trim());
          if (qaLabelTypeAllowsTm(labelType)) {
            matchingTmTextsByType[labelType].add(label.tm == null ? '' : String(label.tm));
          }
          if (label.appliesToResults) {
            resultLabelCountByType[labelType] += 1;
          }
        });
      }

      const readOnlyCoverage = hasWritableScope
        ? buildQaLabelConcreteRepCoverage(
            qaLabels.filter(label => label.labelType === labelType && label.scope !== resolvedScope),
            selectionState.selectedConcreteReps,
          )
        : focusedWellCoverage;

      coverageByType[labelType] = coveredEntryKeysByType[labelType].size;
      nextReadOnlyCoverageByType[labelType] = readOnlyCoverage.coverageByType[labelType];
      readOnlyResultCountsByType[labelType] = readOnlyCoverage.resultCoverageByType[labelType];
      nextHydratedNoteByLabelType[labelType] =
        matchingNotesByType[labelType].size === 1 ? Array.from(matchingNotesByType[labelType])[0] : '';
      if (!qaLabelTypeAllowsTm(labelType)) {
        return;
      }

      nextHydratedTmByLabelType[labelType] =
        matchingTmTextsByType[labelType].size === 1 ? Array.from(matchingTmTextsByType[labelType])[0] : '';
    });

    return {
      labelCoverageByType: coverageByType,
      readOnlyCoverageByType: nextReadOnlyCoverageByType,
      readOnlyResultCountByType: readOnlyResultCountsByType,
      matchingLabelIdsByType: labelIdsByType,
      matchingResultLabelCountByType: resultLabelCountByType,
      hydratedNoteByLabelType: nextHydratedNoteByLabelType,
      hydratedTmByLabelType: nextHydratedTmByLabelType,
    };
  }, [
    entriesByLabelType,
    qaLabels,
    scopeByLabelType,
    selectionState.isAllChipImplicit,
    selectionState.selectedConcreteReps,
  ]);

  useLayoutEffect(() => {
    clearPendingTmEntry();
  }, [chipInfo?.id, selectionContextKey]);

  useEffect(() => {
    setNoteByLabelType(hydratedNoteByLabelType);
    setIsNoteDirtyByLabelType(createQaLabelDirtyMap());
    setTmByLabelType(hydratedTmByLabelType);
    setIsTmDirtyByLabelType(createQaLabelDirtyMap());
    tmAppliesToResultsByLabelTypeRef.current = {};
    pendingMatchingLabelIdsByTypeRef.current = createQaLabelIdMap();
    clearPendingTmEntry();
    // Reset drafts only when the selected scope changes; ordinary label hydration should stay dirty-aware.
    // eslint-disable-next-line exhaustive-deps-with-refs/exhaustive-deps
  }, [selectionContextKey]);

  useEffect(() => {
    setLastUndoAction(null);
    clearPendingTmEntry();
  }, [chipInfo?.id]);

  useEffect(() => {
    setNoteByLabelType(currentNoteByLabelType => {
      const nextNoteByLabelType = qaLabelTypeOrder.reduce<Record<QaLabelType, string>>(
        (acc, labelType) => ({
          ...acc,
          [labelType]: isNoteDirtyByLabelType[labelType]
            ? currentNoteByLabelType[labelType]
            : hydratedNoteByLabelType[labelType],
        }),
        createQaLabelNoteMap(),
      );

      const hasChanges = qaLabelTypeOrder.some(
        labelType => nextNoteByLabelType[labelType] !== currentNoteByLabelType[labelType],
      );
      return hasChanges ? nextNoteByLabelType : currentNoteByLabelType;
    });
  }, [hydratedNoteByLabelType, isNoteDirtyByLabelType]);

  useEffect(() => {
    setTmByLabelType(currentTmByLabelType => {
      const nextTmByLabelType: Partial<Record<QaLabelType, string>> = {
        [QaLabelType.PRODUCT]: isTmDirtyByLabelType[QaLabelType.PRODUCT]
          ? currentTmByLabelType[QaLabelType.PRODUCT] ?? ''
          : hydratedTmByLabelType[QaLabelType.PRODUCT],
        [QaLabelType.PRIMER_DIMER]: isTmDirtyByLabelType[QaLabelType.PRIMER_DIMER]
          ? currentTmByLabelType[QaLabelType.PRIMER_DIMER] ?? ''
          : hydratedTmByLabelType[QaLabelType.PRIMER_DIMER],
        [QaLabelType.UNSPECIFIC]: isTmDirtyByLabelType[QaLabelType.UNSPECIFIC]
          ? currentTmByLabelType[QaLabelType.UNSPECIFIC] ?? ''
          : hydratedTmByLabelType[QaLabelType.UNSPECIFIC],
      };

      const hasChanges =
        nextTmByLabelType[QaLabelType.PRODUCT] !== (currentTmByLabelType[QaLabelType.PRODUCT] ?? '') ||
        nextTmByLabelType[QaLabelType.PRIMER_DIMER] !== (currentTmByLabelType[QaLabelType.PRIMER_DIMER] ?? '') ||
        nextTmByLabelType[QaLabelType.UNSPECIFIC] !== (currentTmByLabelType[QaLabelType.UNSPECIFIC] ?? '');

      return hasChanges ? nextTmByLabelType : currentTmByLabelType;
    });
  }, [hydratedTmByLabelType, isTmDirtyByLabelType]);

  useLayoutEffect(() => {
    tmByLabelTypeRef.current = tmByLabelType;
  }, [tmByLabelType]);

  useLayoutEffect(() => {
    if (pendingTmFocusLabelType == null) {
      return;
    }

    let isCancelled = false;
    const focusPendingInput = () => {
      if (isCancelled) {
        return;
      }

      const pendingEntry = pendingTmEntryRef.current;
      if (pendingEntry == null || pendingEntry.labelType !== pendingTmFocusLabelType) {
        return;
      }

      const input = tmInputRefs.current[pendingTmFocusLabelType];
      if (input == null || input.disabled) {
        requestAnimationFrame(focusPendingInput);
        return;
      }

      input.focus({ preventScroll: true });
      const { selectionStart, selectionEnd } = getQaLabelTmInputSelectionRange(input.value, pendingEntry.replaceDraft);
      input.setSelectionRange(selectionStart, selectionEnd);
      if (document.activeElement === input) {
        clearPendingTmEntry(pendingTmFocusLabelType);
        return;
      }

      requestAnimationFrame(focusPendingInput);
    };

    focusPendingInput();
    return () => {
      isCancelled = true;
    };
  }, [
    activeMutatingLabelType,
    activeNoteSavingLabelType,
    activeTmSavingLabelType,
    chipInfo?.id,
    pendingTmFocusLabelType,
    selectionContextKey,
  ]);

  const handleKeyDownStable = useCallback((event: KeyboardEvent) => {
    if (event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (
      target != null &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.tagName === 'BUTTON' ||
        target.isContentEditable)
    ) {
      return;
    }

    const shortcutState = keyboardShortcutStateRef.current;
    if (shortcutState == null) {
      return;
    }

    if (pendingTmEntryRef.current != null) {
      const tmDigit = getQaLabelTmShortcutDigit(event.code, event.key);
      if (tmDigit != null) {
        event.preventDefault();
        shortcutState.handlePendingTmDigit(tmDigit);
        return;
      }

      if (getQaLabelShortcutDigit(event.code, event.key) != null) {
        event.preventDefault();
        return;
      }
    }

    const labelType = shortcutToLabelType(event.code, event.key);
    if (labelType == null) {
      return;
    }

    const {
      applyLabel: applyLabelFromShortcutState,
      applyTmLabel: applyTmLabelFromShortcutState,
      matchingLabelIdsByType: matchingLabelIdsByTypeFromShortcutState,
      queueQaLabelMutation: queueQaLabelMutationFromShortcutState,
      readOnlyCoverageByType: readOnlyCoverageByTypeFromShortcutState,
    } = shortcutState;

    if (
      readOnlyCoverageByTypeFromShortcutState[labelType] > 0 &&
      matchingLabelIdsByTypeFromShortcutState[labelType].length === 0
    ) {
      return;
    }

    event.preventDefault();
    if (qaLabelTypeAllowsTm(labelType)) {
      void queueQaLabelMutationFromShortcutState(labelType, async () => {
        await applyTmLabelFromShortcutState(labelType, event.shiftKey);
      });
      return;
    }

    void queueQaLabelMutationFromShortcutState(labelType, async () => {
      await applyLabelFromShortcutState(labelType, event.shiftKey);
    });
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDownStable);
    return () => {
      window.removeEventListener('keydown', handleKeyDownStable);
    };
  }, [handleKeyDownStable]);

  async function queueQaMutation<T>(task: () => Promise<T>): Promise<T> {
    const queuedTask = queuedQaMutationRef.current.then(
      async () => await task(),
      async () => await task(),
    );
    queuedQaMutationRef.current = queuedTask.then(
      () => undefined,
      () => undefined,
    );
    return await queuedTask;
  }

  async function queueQaLabelMutation<T>(labelType: QaLabelType, task: () => Promise<T>): Promise<T> {
    return await queueQaMutation(async () => {
      setActiveMutatingLabelType(labelType);
      try {
        return await task();
      } finally {
        setActiveMutatingLabelType(current => (current === labelType ? null : current));
      }
    });
  }

  function getResolvedQaSelection(labelType: QaLabelType): {
    resolvedScope: QaLabelScope | null;
    currentEntries: QaLabelScopeEntry[];
  } {
    return {
      resolvedScope: scopeByLabelType[labelType],
      currentEntries: entriesByLabelType[labelType],
    };
  }

  function hasFullScopeCoverage(labelType: QaLabelType): boolean {
    const { currentEntries } = getResolvedQaSelection(labelType);
    return currentEntries.length > 0 && labelCoverageByType[labelType] === currentEntries.length;
  }

  function clearPendingTmEntry(labelType?: QaLabelType) {
    const pendingEntry = pendingTmEntryRef.current;
    if (pendingEntry == null) {
      return;
    }

    if (labelType != null && pendingEntry.labelType !== labelType) {
      return;
    }

    pendingTmEntryRef.current = null;
    setPendingTmFocusLabelType(current => (labelType == null || current === labelType ? null : current));
  }

  function focusTmInput(labelType: QaLabelType) {
    pendingTmEntryRef.current = {
      labelType,
      replaceDraft: true,
    };
    setPendingTmFocusLabelType(labelType);
  }

  function handlePendingTmShortcutDigit(digit: string) {
    const pendingEntry = pendingTmEntryRef.current;
    if (pendingEntry == null) {
      return;
    }

    const nextDraft = appendQaLabelTmShortcutDigit(
      tmByLabelTypeRef.current[pendingEntry.labelType] ?? '',
      digit,
      pendingEntry.replaceDraft,
    );
    pendingTmEntryRef.current = {
      ...pendingEntry,
      replaceDraft: nextDraft.replaceDraft,
    };
    setTmDirty(pendingEntry.labelType, true);
    setTmByLabelType(currentTmByLabelType => {
      if ((currentTmByLabelType[pendingEntry.labelType] ?? '') === nextDraft.nextTmText) {
        return currentTmByLabelType;
      }

      return {
        ...currentTmByLabelType,
        [pendingEntry.labelType]: nextDraft.nextTmText,
      };
    });

    if (nextDraft.nextTmText.length !== 2 || !isQaLabelTmInRange(Number(nextDraft.nextTmText))) {
      return;
    }

    clearPendingTmEntry(pendingEntry.labelType);
    void queueQaLabelMutation(pendingEntry.labelType, async () => {
      await saveTmLabel(
        pendingEntry.labelType,
        resolveTmAppliesToResults(pendingEntry.labelType),
        nextDraft.nextTmText,
      );
    });
  }

  function getKnownMatchingLabelIds(labelType: QaLabelType): number[] {
    return Array.from(
      new Set([...matchingLabelIdsByType[labelType], ...pendingMatchingLabelIdsByTypeRef.current[labelType]]),
    );
  }

  function getResultStatusApplyPlan(labelType: QaLabelType) {
    const { resolvedScope, currentEntries } = getResolvedQaSelection(labelType);
    if (resolvedScope == null || currentEntries.length === 0) {
      return {
        labelIdsToEnable: [],
        missingEntries: [],
      };
    }

    return buildQaLabelResultStatusApplyPlan(getCurrentChipQaLabels(), resolvedScope, labelType, currentEntries);
  }

  function resolveTmAppliesToResults(labelType: QaLabelType): boolean {
    return tmAppliesToResultsByLabelTypeRef.current[labelType] ?? matchingResultLabelCountByType[labelType] > 0;
  }

  function rememberTmAppliesToResults(labelType: QaLabelType, appliesToResults: boolean) {
    tmAppliesToResultsByLabelTypeRef.current[labelType] = appliesToResults;
  }

  function setNoteDirty(labelType: QaLabelType, isDirty: boolean) {
    setIsNoteDirtyByLabelType(current => {
      if (current[labelType] === isDirty) {
        return current;
      }

      return {
        ...current,
        [labelType]: isDirty,
      };
    });
  }

  function setTmDirty(labelType: QaLabelType, isDirty: boolean) {
    setIsTmDirtyByLabelType(current => {
      if (current[labelType] === isDirty) {
        return current;
      }

      return {
        ...current,
        [labelType]: isDirty,
      };
    });
  }

  function clearNoteDraft(labelType: QaLabelType) {
    setNoteDirty(labelType, false);
    setNoteByLabelType(current => {
      if (current[labelType] === '') {
        return current;
      }

      return {
        ...current,
        [labelType]: '',
      };
    });
  }

  function clearAllNoteDrafts() {
    setNoteByLabelType(createQaLabelNoteMap());
    setIsNoteDirtyByLabelType(createQaLabelDirtyMap());
  }

  function clearTmDrafts() {
    setTmByLabelType({});
    setIsTmDirtyByLabelType(createQaLabelDirtyMap());
    tmAppliesToResultsByLabelTypeRef.current = {};
    clearPendingTmEntry();
  }

  function clearPendingMatchingLabelIds() {
    pendingMatchingLabelIdsByTypeRef.current = createQaLabelIdMap();
  }

  function hasDirtyTmDrafts() {
    return Object.values(isTmDirtyByLabelType).some(Boolean);
  }

  function hasValidQaSelection(labelType: QaLabelType) {
    if (selectionState.isAllChipImplicit && !isImplicitWholeChipQaLabelAllowed(labelType)) {
      toast({
        title: 'Select at least one sample or assay before labeling',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      return false;
    }

    const { resolvedScope, currentEntries } = getResolvedQaSelection(labelType);
    if (resolvedScope == null || currentEntries.length === 0) {
      toast({
        title: `Current selection cannot be expressed as a valid ${qaLabelDefinitions[labelType].title} scope`,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      return false;
    }

    return true;
  }

  function parseValidatedTm(
    labelType: QaLabelType,
    tmText: string,
    showErrorToast: boolean,
  ): number | null | undefined {
    const trimmedTmText = tmText.trim();
    if (trimmedTmText.length === 0) {
      return null;
    }

    const tm = Number(trimmedTmText);
    if (!Number.isInteger(tm)) {
      if (showErrorToast) {
        toast({
          title: `Invalid Tm for ${qaLabelDefinitions[labelType].title}`,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
      }
      return undefined;
    }

    if (!isQaLabelTmInRange(tm)) {
      if (showErrorToast) {
        toast({
          title: `${qaLabelDefinitions[labelType].title} Tm must be between ${QA_LABEL_TM_MIN} and ${QA_LABEL_TM_MAX}`,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
      }
      return undefined;
    }

    return tm;
  }

  function getCurrentQaLabelUrl(): string {
    return chipInfo ? buildQaLabelChipUrl(chipInfo.name, window.location) : window.location.href;
  }

  function getCurrentChipQaLabels(): QaLabel[] {
    if (!chipInfo) {
      return qaLabels;
    }

    return queryClient.getQueryData<ChipDebugResponse>([`/chip/${chipInfo.id}`])?.qaLabels ?? qaLabels;
  }

  async function createQaLabelBatches(batches: QaLabelRestoreBatch[]): Promise<QaLabelBatchResponse[]> {
    if (!chipInfo) {
      return [];
    }

    const responses: QaLabelBatchResponse[] = [];
    for (const batch of batches) {
      responses.push(
        await createBatch.mutateAsync({
          chipId: chipInfo.id,
          chipName: chipInfo.name,
          url: batch.url,
          scope: batch.scope,
          entries: batch.entries,
          labelType: batch.labelType,
          tm: batch.tm,
          note: batch.note,
          appliesToResults: batch.appliesToResults,
        }),
      );
    }

    return responses;
  }

  async function restoreDeletedLabels(labels: QaLabel[]): Promise<QaLabel[]> {
    const restoreResponses = await createQaLabelBatches(buildQaLabelRestoreBatches(labels));
    return restoreResponses.flatMap(response => response.labels);
  }

  async function undoCreatedBatches(batchIds: string[]): Promise<void> {
    if (!chipInfo) {
      return;
    }

    for (const batchId of batchIds) {
      await undoBatch.mutateAsync({
        chipId: chipInfo.id,
        chipName: chipInfo.name,
        batchId,
      });
    }
  }

  async function createLabelBatch(
    labelType: QaLabelType,
    appliesToResults: boolean,
    tmText: string,
    options: {
      entries?: QaLabelScopeEntry[];
      noteText?: string;
      showSuccessToast: boolean;
      showValidationToast: boolean;
      url?: string;
    },
  ) {
    if (!chipInfo) {
      return false;
    }

    if (appliesToResults && !doesQaLabelTypeAllowResultApplication(labelType)) {
      toast({
        title: getQaInvalidResultApplicationMessage(labelType),
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      return false;
    }

    const { resolvedScope, currentEntries } = getResolvedQaSelection(labelType);
    const targetEntries = options.entries ?? currentEntries;
    if (!hasValidQaSelection(labelType) || resolvedScope == null || targetEntries.length === 0) {
      return false;
    }

    const tm = parseValidatedTm(labelType, tmText, options.showValidationToast);
    if (tm === undefined) {
      return false;
    }

    try {
      const response = await createBatch.mutateAsync({
        chipId: chipInfo.id,
        chipName: chipInfo.name,
        url: options.url ?? getCurrentQaLabelUrl(),
        scope: resolvedScope,
        entries: targetEntries,
        labelType,
        tm,
        note: options.noteText ?? '',
        appliesToResults,
      });

      updateChipQaLabelsCache(queryClient, chipInfo.id, currentLabels => mergeQaLabels(currentLabels, response.labels));
      pendingMatchingLabelIdsByTypeRef.current[labelType] = response.labels.map(label => label.id);
      setLastUndoAction({
        type: 'created-batches',
        batchIds: [response.batchId],
        deletedLabelsToRestore: [],
      });
      if (qaLabelTypeAllowsTm(labelType) && tm != null) {
        setTmDirty(labelType, false);
      }
      void invalidateChipQaQueries(queryClient, chipInfo.id);

      if (options.showSuccessToast) {
        const broaderThanSingleTriplicate = resolvedScope !== QaLabelScope.TRIPLICATE || targetEntries.length !== 1;
        const skippedEntryCount = targetEntries.length - response.labels.length;
        const savedLabelCount = response.labels.length;
        toast({
          title:
            skippedEntryCount > 0
              ? `Saved ${savedLabelCount} new QA label${savedLabelCount === 1 ? '' : 's'}; skipped ${skippedEntryCount} existing entr${
                  skippedEntryCount === 1 ? 'y' : 'ies'
                }`
              : broaderThanSingleTriplicate
                ? `Saved ${savedLabelCount} QA labels to ${targetEntries.length} ${qaScopeLabels[resolvedScope].toLowerCase()} entries`
                : `Saved ${qaLabelDefinitions[labelType].title} to one triplicate`,
          status: skippedEntryCount > 0 || broaderThanSingleTriplicate ? 'warning' : 'success',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
      }

      return true;
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : 'Failed to save QA labels',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      return false;
    }
  }

  async function deleteMatchingLabelsForType(
    labelType: QaLabelType,
    options: {
      showSuccessToast: boolean;
      labelIds?: number[];
      recordUndoAction?: boolean;
      resetNoteDirty?: boolean;
      resetTmDirty?: boolean;
    },
  ) {
    const labelIds = options.labelIds ?? getKnownMatchingLabelIds(labelType);
    if (!chipInfo || labelIds.length === 0) {
      return true;
    }

    const deletedLabelsSnapshot = getCurrentChipQaLabels().filter(label => labelIds.includes(label.id));

    try {
      const response = await deleteLabels.mutateAsync({
        chipId: chipInfo.id,
        chipName: chipInfo.name,
        labelIds,
      });

      pendingMatchingLabelIdsByTypeRef.current[labelType] = [];
      const deletedLabels = deletedLabelsSnapshot.filter(label => response.deletedLabelIds.includes(label.id));
      if (options.recordUndoAction ?? true) {
        setLastUndoAction(deletedLabels.length > 0 ? { type: 'deleted-labels', labels: deletedLabels } : null);
      }
      if (options.resetNoteDirty ?? true) {
        clearNoteDraft(labelType);
      }
      if (qaLabelTypeAllowsTm(labelType) && (options.resetTmDirty ?? true)) {
        setTmDirty(labelType, false);
      }
      updateChipQaLabelsCache(queryClient, chipInfo.id, currentLabels =>
        currentLabels.filter(label => !response.deletedLabelIds.includes(label.id)),
      );
      void invalidateChipQaQueries(queryClient, chipInfo.id);

      const { resolvedScope, currentEntries } = getResolvedQaSelection(labelType);
      if (options.showSuccessToast && resolvedScope != null) {
        const broaderThanSingleTriplicate = resolvedScope !== QaLabelScope.TRIPLICATE || currentEntries.length !== 1;
        const deletedLabelCount = response.deletedLabelIds.length;
        toast({
          title:
            deletedLabelCount === 0
              ? 'No matching QA labels remained to delete'
              : broaderThanSingleTriplicate
                ? `Removed ${deletedLabelCount} QA label${deletedLabelCount === 1 ? '' : 's'} from ${currentEntries.length} ${qaScopeLabels[resolvedScope].toLowerCase()} entries`
                : `Removed ${qaLabelDefinitions[labelType].title} from one triplicate`,
          status: deletedLabelCount === 0 || broaderThanSingleTriplicate ? 'warning' : 'success',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
      }

      return true;
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : 'Failed to delete QA labels',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      return false;
    }
  }

  async function setMatchingLabelsResultStatus(
    labelType: QaLabelType,
    labelIds: number[],
    appliesToResults: boolean,
    options: { showSuccessToast: boolean },
  ) {
    if (!chipInfo || labelIds.length === 0) {
      return true;
    }

    if (appliesToResults && !doesQaLabelTypeAllowResultApplication(labelType)) {
      toast({
        title: getQaInvalidResultApplicationMessage(labelType),
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      return false;
    }

    const existingMatchingLabels = getCurrentChipQaLabels().filter(label => labelIds.includes(label.id));
    const labelIdsToUpdate = existingMatchingLabels
      .filter(label => label.appliesToResults !== appliesToResults)
      .map(label => label.id);

    if (labelIdsToUpdate.length === 0) {
      if (options.showSuccessToast) {
        toast({
          title: appliesToResults
            ? `${qaLabelDefinitions[labelType].title} already affects results`
            : `${qaLabelDefinitions[labelType].title} is already training only`,
          status: 'warning',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
      }

      return true;
    }

    try {
      const response = await toggleResultStatus.mutateAsync({
        chipId: chipInfo.id,
        chipName: chipInfo.name,
        labelIds: labelIdsToUpdate,
        appliesToResults,
      });

      pendingMatchingLabelIdsByTypeRef.current[labelType] = response.labels.map(label => label.id);
      tmAppliesToResultsByLabelTypeRef.current[labelType] = undefined;
      updateChipQaLabelsCache(queryClient, chipInfo.id, currentLabels => mergeQaLabels(currentLabels, response.labels));
      void invalidateChipQaQueries(queryClient, chipInfo.id);

      if (options.showSuccessToast) {
        toast({
          title: appliesToResults
            ? `Changed ${qaLabelDefinitions[labelType].title} to affect results`
            : `Changed ${qaLabelDefinitions[labelType].title} to training only`,
          status: 'success',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
      }

      return true;
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : 'Failed to toggle QA label result status',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      return false;
    }
  }

  async function applyLabel(labelType: QaLabelType, appliesToResults: boolean) {
    if (!chipInfo) {
      return;
    }

    if (!hasValidQaSelection(labelType)) {
      return;
    }

    if (appliesToResults) {
      const applyPlan = getResultStatusApplyPlan(labelType);
      const toggledExistingLabels = await setMatchingLabelsResultStatus(labelType, applyPlan.labelIdsToEnable, true, {
        showSuccessToast: false,
      });
      if (!toggledExistingLabels) {
        return;
      }

      const createdMissingEntries =
        applyPlan.missingEntries.length === 0
          ? true
          : await createLabelBatch(labelType, true, tmByLabelType[labelType] ?? '', {
              entries: applyPlan.missingEntries,
              showSuccessToast: false,
              showValidationToast: true,
            });
      if (!createdMissingEntries) {
        if (applyPlan.labelIdsToEnable.length > 0) {
          await setMatchingLabelsResultStatus(labelType, applyPlan.labelIdsToEnable, false, {
            showSuccessToast: false,
          });
        }
        return;
      }

      toast({
        title:
          applyPlan.labelIdsToEnable.length === 0 && applyPlan.missingEntries.length === 0
            ? `${qaLabelDefinitions[labelType].title} already affects results`
            : applyPlan.missingEntries.length === 0
              ? `Changed ${qaLabelDefinitions[labelType].title} to affect results`
              : `Changed ${qaLabelDefinitions[labelType].title} to affect results and filled ${applyPlan.missingEntries.length} unlabeled entr${
                  applyPlan.missingEntries.length === 1 ? 'y' : 'ies'
                }`,
        status:
          applyPlan.labelIdsToEnable.length === 0 && applyPlan.missingEntries.length === 0 ? 'warning' : 'success',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    const matchingLabelIds = getKnownMatchingLabelIds(labelType);
    if (hasFullScopeCoverage(labelType) && matchingLabelIds.length > 0) {
      await deleteMatchingLabelsForType(labelType, { showSuccessToast: true, labelIds: matchingLabelIds });
      return;
    }

    await createLabelBatch(labelType, appliesToResults, tmByLabelType[labelType] ?? '', {
      showSuccessToast: true,
      showValidationToast: true,
    });
  }

  async function applyTmLabel(labelType: QaLabelType, appliesToResults: boolean) {
    if (!chipInfo) {
      return;
    }

    const { currentEntries } = getResolvedQaSelection(labelType);
    if (!hasValidQaSelection(labelType)) {
      return;
    }

    const matchingLabelIds = getKnownMatchingLabelIds(labelType);
    if (appliesToResults) {
      const applyPlan = getResultStatusApplyPlan(labelType);
      const toggledExistingLabels = await setMatchingLabelsResultStatus(labelType, applyPlan.labelIdsToEnable, true, {
        showSuccessToast: false,
      });
      if (!toggledExistingLabels) {
        return;
      }

      rememberTmAppliesToResults(labelType, true);
      focusTmInput(labelType);

      if (applyPlan.missingEntries.length === 0) {
        return;
      }

      const createdMissingEntries = await createLabelBatch(labelType, true, '', {
        entries: applyPlan.missingEntries,
        showSuccessToast: false,
        showValidationToast: false,
      });
      if (!createdMissingEntries && applyPlan.labelIdsToEnable.length > 0) {
        await setMatchingLabelsResultStatus(labelType, applyPlan.labelIdsToEnable, false, { showSuccessToast: false });
      }
      return;
    }

    if (hasFullScopeCoverage(labelType) && matchingLabelIds.length > 0) {
      await deleteMatchingLabelsForType(labelType, {
        showSuccessToast: true,
        labelIds: matchingLabelIds,
      });
      return;
    }

    rememberTmAppliesToResults(labelType, appliesToResults);
    focusTmInput(labelType);

    if (labelCoverageByType[labelType] === currentEntries.length) {
      return;
    }

    await createLabelBatch(labelType, appliesToResults, '', {
      showSuccessToast: false,
      showValidationToast: false,
    });
  }

  keyboardShortcutStateRef.current = {
    applyLabel,
    applyTmLabel,
    handlePendingTmDigit: digit => {
      handlePendingTmShortcutDigit(digit);
    },
    matchingLabelIdsByType,
    queueQaLabelMutation: async (labelType, task) => {
      await queueQaLabelMutation(labelType, task);
    },
    readOnlyCoverageByType,
  };

  async function saveTmLabel(labelType: QaLabelType, appliesToResults: boolean, tmText: string) {
    const tm = parseValidatedTm(labelType, tmText, false);
    if (tm == null) {
      return;
    }

    if (!chipInfo) {
      return;
    }

    const { resolvedScope, currentEntries } = getResolvedQaSelection(labelType);
    const matchingLabelIds = getKnownMatchingLabelIds(labelType);
    const matchingLabelsSnapshot = getCurrentChipQaLabels().filter(label => matchingLabelIds.includes(label.id));
    const rewriteBatches =
      matchingLabelsSnapshot.length > 0
        ? buildQaLabelTmRewriteBatches(matchingLabelsSnapshot, tm)
        : resolvedScope == null || currentEntries.length === 0
          ? []
          : [
              {
                scope: resolvedScope,
                labelType,
                appliesToResults,
                tm,
                note: noteByLabelType[labelType].trim(),
                url: getCurrentQaLabelUrl(),
                entries: currentEntries,
              },
            ];
    if (rewriteBatches.length === 0) {
      return;
    }

    setActiveTmSavingLabelType(labelType);
    let recreatedResponses: QaLabelBatchResponse[] = [];
    try {
      if (matchingLabelIds.length > 0) {
        const deleted = await deleteMatchingLabelsForType(labelType, {
          showSuccessToast: false,
          labelIds: matchingLabelIds,
          recordUndoAction: false,
          resetNoteDirty: false,
          resetTmDirty: false,
        });
        if (!deleted) {
          return;
        }
      }

      recreatedResponses = await createQaLabelBatches(rewriteBatches);
      const recreatedLabels = recreatedResponses.flatMap(response => response.labels);
      pendingMatchingLabelIdsByTypeRef.current[labelType] = recreatedLabels.map(label => label.id);
      setLastUndoAction({
        type: 'created-batches',
        batchIds: recreatedResponses.map(response => response.batchId),
        deletedLabelsToRestore: matchingLabelsSnapshot,
      });
      setTmDirty(labelType, false);
      updateChipQaLabelsCache(queryClient, chipInfo.id, currentLabels => mergeQaLabels(currentLabels, recreatedLabels));
      void invalidateChipQaQueries(queryClient, chipInfo.id);
    } catch (error) {
      const recreatedBatchIds = recreatedResponses.map(response => response.batchId);
      try {
        if (recreatedBatchIds.length > 0) {
          await undoCreatedBatches(recreatedBatchIds);
        }

        if (matchingLabelsSnapshot.length > 0) {
          const restoredLabels = await restoreDeletedLabels(matchingLabelsSnapshot);
          pendingMatchingLabelIdsByTypeRef.current[labelType] = restoredLabels.map(label => label.id);
          updateChipQaLabelsCache(queryClient, chipInfo.id, currentLabels =>
            mergeQaLabels(currentLabels, restoredLabels),
          );
        }
      } catch (rollbackError) {
        setLastUndoAction(null);
        clearPendingMatchingLabelIds();
        void invalidateChipQaQueries(queryClient, chipInfo.id);
        toast({
          title:
            rollbackError instanceof Error
              ? `${error instanceof Error ? error.message : 'Failed to save QA labels'}; rollback also failed: ${rollbackError.message}`
              : error instanceof Error
                ? `${error.message}; rollback also failed`
                : 'Failed to save QA labels',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        return;
      }

      void invalidateChipQaQueries(queryClient, chipInfo.id);
      toast({
        title: error instanceof Error ? error.message : 'Failed to save QA labels',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setActiveTmSavingLabelType(current => (current === labelType ? null : current));
    }
  }

  async function saveNoteLabel(labelType: QaLabelType, noteText: string) {
    if (!chipInfo) {
      return;
    }

    const labelIds = getKnownMatchingLabelIds(labelType);
    if (labelIds.length === 0) {
      clearNoteDraft(labelType);
      return;
    }

    setActiveNoteSavingLabelType(labelType);
    try {
      const response = await updateNotes.mutateAsync({
        chipId: chipInfo.id,
        chipName: chipInfo.name,
        labelIds,
        note: noteText,
      });

      pendingMatchingLabelIdsByTypeRef.current[labelType] = response.labels.map(label => label.id);
      setNoteDirty(labelType, false);
      setNoteByLabelType(current => ({
        ...current,
        [labelType]: response.labels[0]?.note.trim() ?? noteText.trim(),
      }));
      updateChipQaLabelsCache(queryClient, chipInfo.id, currentLabels => mergeQaLabels(currentLabels, response.labels));
      void invalidateChipQaQueries(queryClient, chipInfo.id);
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : 'Failed to save QA note',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setActiveNoteSavingLabelType(current => (current === labelType ? null : current));
    }
  }

  async function submitNoteIfDirty(labelType: QaLabelType) {
    if (!isNoteDirtyByLabelType[labelType]) {
      return;
    }

    await saveNoteLabel(labelType, noteByLabelType[labelType]);
  }

  async function undoLastBatch() {
    if (lastUndoAction == null) {
      if (hasDirtyTmDrafts()) {
        clearTmDrafts();
      }
      return;
    }

    if (!chipInfo) {
      return;
    }

    if (lastUndoAction.type === 'deleted-labels') {
      setIsRestoringDeletedLabels(true);
      try {
        const restoredLabels = await restoreDeletedLabels(lastUndoAction.labels);

        setLastUndoAction(null);
        clearPendingMatchingLabelIds();
        clearAllNoteDrafts();
        clearTmDrafts();
        updateChipQaLabelsCache(queryClient, chipInfo.id, currentLabels =>
          mergeQaLabels(currentLabels, restoredLabels),
        );
        void invalidateChipQaQueries(queryClient, chipInfo.id);
        toast({
          title:
            restoredLabels.length === 0
              ? 'Matching QA labels were already restored'
              : `Restored ${restoredLabels.length} QA label${restoredLabels.length === 1 ? '' : 's'}`,
          status: restoredLabels.length === 0 ? 'warning' : 'success',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
      } catch (error) {
        void invalidateChipQaQueries(queryClient, chipInfo.id);
        toast({
          title: error instanceof Error ? error.message : 'Failed to restore QA labels',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } finally {
        setIsRestoringDeletedLabels(false);
      }
      return;
    }

    const shouldShowRestoreLoading = lastUndoAction.deletedLabelsToRestore.length > 0;
    if (shouldShowRestoreLoading) {
      setIsRestoringDeletedLabels(true);
    }

    try {
      await undoCreatedBatches(lastUndoAction.batchIds);
      const restoredLabels =
        lastUndoAction.deletedLabelsToRestore.length > 0
          ? await restoreDeletedLabels(lastUndoAction.deletedLabelsToRestore)
          : [];
      const batchIdsToRemove = new Set(lastUndoAction.batchIds);

      setLastUndoAction(null);
      clearPendingMatchingLabelIds();
      clearAllNoteDrafts();
      clearTmDrafts();
      updateChipQaLabelsCache(queryClient, chipInfo.id, currentLabels =>
        mergeQaLabels(
          currentLabels.filter(label => !batchIdsToRemove.has(label.labelBatchId)),
          restoredLabels,
        ),
      );
      void invalidateChipQaQueries(queryClient, chipInfo.id);
      toast({
        title:
          restoredLabels.length > 0
            ? `Restored ${restoredLabels.length} QA label${restoredLabels.length === 1 ? '' : 's'}`
            : 'Undid the last QA label batch',
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
    } catch (error) {
      void invalidateChipQaQueries(queryClient, chipInfo.id);
      toast({
        title: error instanceof Error ? error.message : 'Failed to undo QA labels',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      if (shouldShowRestoreLoading) {
        setIsRestoringDeletedLabels(false);
      }
    }
  }

  const isMutatingLabels =
    createBatch.isPending ||
    deleteLabels.isPending ||
    toggleResultStatus.isPending ||
    updateNotes.isPending ||
    isRestoringDeletedLabels;
  const qaControlsDisabled = undoBatch.isPending || isRestoringDeletedLabels;
  const hasTmDraft = hasDirtyTmDrafts();
  const showUndoButton = hasTmDraft || lastUndoAction != null;
  const writableSelections = qaLabelTypeOrder
    .map(labelType => ({
      labelType,
      ...getResolvedQaSelection(labelType),
    }))
    .filter(selection => selection.resolvedScope != null && selection.currentEntries.length > 0);
  const selectionSummary = selectionState.isAllChipImplicit
    ? 'Whole chip selected. Only Issue and Other can be applied at CHIP scope without an explicit subset.'
    : writableSelections.length === 0
      ? 'Current filters do not map to a valid QA label scope.'
      : new Set(
            writableSelections.map(
              selection =>
                `${selection.resolvedScope}::${selection.currentEntries
                  .map(entry => buildQaLabelScopeEntryKey(selection.resolvedScope as QaLabelScope, entry))
                  .join('|')}`,
            ),
          ).size === 1
        ? `${qaScopeLabels[writableSelections[0].resolvedScope as QaLabelScope]} scope, ${writableSelections[0].currentEntries.length} ${
            writableSelections[0].currentEntries.length === 1 ? 'entry' : 'entries'
          } selected.`
        : 'Scope varies by label type for the current selection.';

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="white" px={4} py={3}>
      <Flex
        align={{ base: 'flex-start', xl: 'center' }}
        direction={{ base: 'column', xl: 'row' }}
        gap={1}
        justify="space-between"
      >
        <Box minWidth={0}>
          <Text fontSize="sm" fontWeight={700}>
            QA verdict
          </Text>
          <Text fontSize="xs" color="gray.600">
            {selectionSummary}
          </Text>
          <Text fontSize="xs" color="gray.500">
            Labels should represent your best judgement of the underlying biological reality (or uncertainty) based on
            the curves and everything else you know about the sample, assay, chip, reagents, run and SmartChip system
            state. See{' '}
            <Link href={standardQaNotesUrl} isExternal color="blue.600" fontWeight={600}>
              keywords for notes
            </Link>
            .
          </Text>
        </Box>
      </Flex>
      <Flex mt={3} wrap="wrap" columnGap={3} rowGap={2} align="flex-start">
        {qaLabelTypeOrder.map(labelType => {
          const definition = qaLabelDefinitions[labelType];
          const { resolvedScope, currentEntries } = getResolvedQaSelection(labelType);
          const noteValue = noteByLabelType[labelType];
          const tmValue = tmByLabelType[labelType] ?? '';
          const tmIsInvalid = tmValue.length === 2 && !isQaLabelTmInRange(Number(tmValue));
          const coveredCount = labelCoverageByType[labelType];
          const readOnlyCoverageCount = readOnlyCoverageByType[labelType];
          const isReadOnlyCoverage = coveredCount === 0 && readOnlyCoverageCount > 0;
          const isScopeUnavailable = resolvedScope == null || currentEntries.length === 0;
          const isImplicitWholeChipDisabled =
            selectionState.isAllChipImplicit && !isImplicitWholeChipQaLabelAllowed(labelType);
          const hasVisibleNoteField = coveredCount > 0;
          const buttonWidth = labelType === QaLabelType.ISSUE ? '136px' : '116px';
          const cellWidth = definition.allowsTm ? '180px' : buttonWidth;
          const coverageState = isReadOnlyCoverage
            ? getQaLabelCoverageState(readOnlyCoverageCount, selectionState.selectedConcreteReps.length)
            : getQaLabelCoverageState(coveredCount, currentEntries.length);
          const resultCoverageState = getQaLabelCoverageState(
            isReadOnlyCoverage ? readOnlyResultCountByType[labelType] : matchingResultLabelCountByType[labelType],
            isReadOnlyCoverage ? selectionState.selectedConcreteReps.length : currentEntries.length,
          );
          const appearance = getQaLabelButtonAppearance(labelType, coverageState, isReadOnlyCoverage);
          const resultIndicator = getQaLabelResultIndicatorAppearance(resultCoverageState);
          const isCurrentLabelMutating =
            (createBatch.isPending || deleteLabels.isPending || toggleResultStatus.isPending) &&
            activeMutatingLabelType === labelType;
          const isCurrentNoteSaving = activeNoteSavingLabelType === labelType;
          const isAnotherLabelMutating =
            ((createBatch.isPending || deleteLabels.isPending || toggleResultStatus.isPending) &&
              activeMutatingLabelType !== labelType) ||
            (activeNoteSavingLabelType != null && activeNoteSavingLabelType !== labelType) ||
            (activeTmSavingLabelType != null && activeTmSavingLabelType !== labelType);
          const noteTooltipLabel = hasVisibleNoteField
            ? getQaLabelerTooltipLabel(
                qaLabels
                  .filter(label => matchingLabelIdsByType[labelType].includes(label.id))
                  .map(label => label.labelerFirstName),
              )
            : null;
          const noteInput = hasVisibleNoteField ? (
            <Tooltip
              label={noteTooltipLabel}
              hasArrow
              placement="top"
              openDelay={200}
              isDisabled={noteTooltipLabel == null}
            >
              <Box width="full">
                <Input
                  size="sm"
                  height="28px"
                  width="full"
                  px={2}
                  fontSize="11px"
                  placeholder="Note"
                  value={noteValue}
                  isDisabled={
                    qaControlsDisabled ||
                    isImplicitWholeChipDisabled ||
                    isAnotherLabelMutating ||
                    activeNoteSavingLabelType != null ||
                    activeTmSavingLabelType != null
                  }
                  onChange={event => {
                    setNoteDirty(labelType, true);
                    setNoteByLabelType(current => ({
                      ...current,
                      [labelType]: event.target.value,
                    }));
                  }}
                  onBlur={() => {
                    void queueQaMutation(async () => {
                      await submitNoteIfDirty(labelType);
                    });
                  }}
                  onKeyDown={event => {
                    if (event.key !== 'Enter') {
                      return;
                    }

                    event.preventDefault();
                    event.currentTarget.blur();
                  }}
                />
              </Box>
            </Tooltip>
          ) : null;
          return (
            <Box key={labelType} width={cellWidth}>
              <Flex gap={2} align="center">
                <Tooltip label={definition.description} hasArrow placement="top" openDelay={200}>
                  <Box
                    display="inline-flex"
                    borderWidth={resultIndicator.borderWidth}
                    borderStyle={resultIndicator.borderStyle}
                    borderColor={resultIndicator.borderColor}
                    borderRadius="md"
                    p={resultIndicator.padding}
                  >
                    <Button
                      size="xs"
                      height="28px"
                      px={3}
                      minW={buttonWidth}
                      justifyContent="center"
                      borderRadius="sm"
                      borderWidth="1px"
                      borderColor={appearance.borderColor}
                      bg={appearance.bg}
                      color={appearance.color}
                      opacity={appearance.opacity}
                      fontSize="11px"
                      fontWeight={800}
                      letterSpacing="0.03em"
                      textTransform="uppercase"
                      whiteSpace="nowrap"
                      onClick={event => {
                        void queueQaLabelMutation(labelType, async () => {
                          if (definition.allowsTm) {
                            await applyTmLabel(labelType, event.shiftKey);
                            return;
                          }

                          await applyLabel(labelType, event.shiftKey);
                        });
                      }}
                      isLoading={isCurrentLabelMutating}
                      isDisabled={
                        qaControlsDisabled ||
                        isImplicitWholeChipDisabled ||
                        isScopeUnavailable ||
                        isAnotherLabelMutating ||
                        isCurrentNoteSaving ||
                        isReadOnlyCoverage
                      }
                      _hover={{
                        bg: appearance.hoverBg,
                        borderColor: appearance.borderColor,
                      }}
                      _disabled={{
                        bg: isAnotherLabelMutating ? 'gray.100' : appearance.bg,
                        color: isAnotherLabelMutating ? 'gray.500' : appearance.color,
                        borderColor: isAnotherLabelMutating ? 'gray.200' : appearance.borderColor,
                        opacity: isAnotherLabelMutating ? 0.8 : appearance.opacity,
                        cursor: 'not-allowed',
                      }}
                    >
                      {`${definition.title} [${definition.shortcut}]`}
                    </Button>
                  </Box>
                </Tooltip>
                {definition.allowsTm && (
                  <Input
                    size="sm"
                    height="28px"
                    width="56px"
                    px={2}
                    fontSize="11px"
                    textAlign="center"
                    placeholder="Tm"
                    inputMode="numeric"
                    maxLength={2}
                    isInvalid={tmIsInvalid}
                    isDisabled={
                      qaControlsDisabled ||
                      isImplicitWholeChipDisabled ||
                      isScopeUnavailable ||
                      isAnotherLabelMutating ||
                      isReadOnlyCoverage ||
                      activeNoteSavingLabelType != null ||
                      activeTmSavingLabelType != null
                    }
                    value={tmValue}
                    ref={node => {
                      tmInputRefs.current[labelType] = node;
                    }}
                    onFocus={() => {
                      clearPendingTmEntry(labelType);
                      rememberTmAppliesToResults(labelType, resolveTmAppliesToResults(labelType));
                    }}
                    onChange={event => {
                      const nextTmText = event.target.value.replace(/\D/g, '').slice(0, 2);
                      setTmDirty(labelType, true);
                      setTmByLabelType(current => ({ ...current, [labelType]: nextTmText }));

                      if (nextTmText.length !== 2) {
                        return;
                      }

                      const nextTm = Number(nextTmText);
                      if (!isQaLabelTmInRange(nextTm)) {
                        return;
                      }

                      void queueQaLabelMutation(labelType, async () => {
                        await saveTmLabel(labelType, resolveTmAppliesToResults(labelType), nextTmText);
                      });
                    }}
                  />
                )}
              </Flex>

              <Box mt={1} minH="28px">
                {noteInput}
              </Box>
            </Box>
          );
        })}
        {showUndoButton && (
          <Box width="76px">
            <Button
              size="sm"
              variant="outline"
              height="28px"
              width="full"
              onClick={() => {
                void undoLastBatch();
              }}
              isDisabled={isMutatingLabels}
              isLoading={undoBatch.isPending || isRestoringDeletedLabels}
            >
              Undo
            </Button>
            <Box mt={1} minH="28px" />
          </Box>
        )}
      </Flex>
    </Box>
  );
}

function shortcutToLabelType(code: string, key: string): QaLabelType | null {
  const shortcut = getQaLabelShortcutDigit(code, key);
  if (shortcut == null) {
    return null;
  }

  return qaLabelTypeOrder.find(labelType => qaLabelDefinitions[labelType].shortcut === shortcut) ?? null;
}

function getQaLabelCoverageState(coveredCount: number, totalCount: number): 'unset' | 'partial' | 'full' {
  if (totalCount === 0 || coveredCount === 0) {
    return 'unset';
  }
  return coveredCount === totalCount ? 'full' : 'partial';
}

function getQaLabelButtonAppearance(
  labelType: QaLabelType,
  coverageState: 'unset' | 'partial' | 'full',
  isReadOnly: boolean,
): {
  bg: string;
  hoverBg: string;
  borderColor: string;
  color: string;
  opacity: number;
} {
  if (coverageState === 'unset') {
    return {
      bg: 'gray.100',
      hoverBg: 'gray.200',
      borderColor: 'gray.200',
      color: 'gray.600',
      opacity: 1,
    };
  }

  const accent =
    labelType === QaLabelType.NO_PRODUCT
      ? { bg: 'blue.500', hoverBg: 'blue.600', borderColor: 'blue.500' }
      : labelType === QaLabelType.PRODUCT
        ? { bg: 'green.500', hoverBg: 'green.600', borderColor: 'green.500' }
        : labelType === QaLabelType.ISSUE
          ? { bg: 'red.500', hoverBg: 'red.600', borderColor: 'red.500' }
          : labelType === QaLabelType.OTHER
            ? { bg: 'gray.700', hoverBg: 'gray.800', borderColor: 'gray.700' }
            : { bg: 'orange.400', hoverBg: 'orange.500', borderColor: 'orange.400' };

  return {
    ...accent,
    color: 'white',
    opacity: isReadOnly ? (coverageState === 'partial' ? 0.4 : 0.65) : coverageState === 'partial' ? 0.55 : 1,
  };
}

function getQaLabelResultIndicatorAppearance(coverageState: 'unset' | 'partial' | 'full'): {
  borderWidth: string;
  borderStyle: 'solid' | 'dashed';
  borderColor: string;
  padding: string;
} {
  if (coverageState === 'unset') {
    return {
      borderWidth: '0px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      padding: '0px',
    };
  }

  return {
    borderWidth: '2px',
    borderStyle: coverageState === 'full' ? 'solid' : 'dashed',
    borderColor: 'gray.900',
    padding: '2px',
  };
}

function createQaLabelCountMap(): Record<QaLabelType, number> {
  return {
    [QaLabelType.NO_PRODUCT]: 0,
    [QaLabelType.PRODUCT]: 0,
    [QaLabelType.PRIMER_DIMER]: 0,
    [QaLabelType.UNSPECIFIC]: 0,
    [QaLabelType.NONSENSE]: 0,
    [QaLabelType.ISSUE]: 0,
    [QaLabelType.OTHER]: 0,
  };
}

function createQaLabelEntryKeyMap(): Record<QaLabelType, Set<string>> {
  return {
    [QaLabelType.NO_PRODUCT]: new Set<string>(),
    [QaLabelType.PRODUCT]: new Set<string>(),
    [QaLabelType.PRIMER_DIMER]: new Set<string>(),
    [QaLabelType.UNSPECIFIC]: new Set<string>(),
    [QaLabelType.NONSENSE]: new Set<string>(),
    [QaLabelType.ISSUE]: new Set<string>(),
    [QaLabelType.OTHER]: new Set<string>(),
  };
}

function createQaLabelEntryMap(): Record<QaLabelType, QaLabelScopeEntry[]> {
  return {
    [QaLabelType.NO_PRODUCT]: [],
    [QaLabelType.PRODUCT]: [],
    [QaLabelType.PRIMER_DIMER]: [],
    [QaLabelType.UNSPECIFIC]: [],
    [QaLabelType.NONSENSE]: [],
    [QaLabelType.ISSUE]: [],
    [QaLabelType.OTHER]: [],
  };
}

function createQaLabelIdMap(): Record<QaLabelType, number[]> {
  return {
    [QaLabelType.NO_PRODUCT]: [],
    [QaLabelType.PRODUCT]: [],
    [QaLabelType.PRIMER_DIMER]: [],
    [QaLabelType.UNSPECIFIC]: [],
    [QaLabelType.NONSENSE]: [],
    [QaLabelType.ISSUE]: [],
    [QaLabelType.OTHER]: [],
  };
}

function createQaLabelScopeMap(): Record<QaLabelType, QaLabelScope | null> {
  return {
    [QaLabelType.NO_PRODUCT]: null,
    [QaLabelType.PRODUCT]: null,
    [QaLabelType.PRIMER_DIMER]: null,
    [QaLabelType.UNSPECIFIC]: null,
    [QaLabelType.NONSENSE]: null,
    [QaLabelType.ISSUE]: null,
    [QaLabelType.OTHER]: null,
  };
}

function createQaLabelNoteMap(): Record<QaLabelType, string> {
  return {
    [QaLabelType.NO_PRODUCT]: '',
    [QaLabelType.PRODUCT]: '',
    [QaLabelType.PRIMER_DIMER]: '',
    [QaLabelType.UNSPECIFIC]: '',
    [QaLabelType.NONSENSE]: '',
    [QaLabelType.ISSUE]: '',
    [QaLabelType.OTHER]: '',
  };
}

function createQaLabelNoteSetMap(): Record<QaLabelType, Set<string>> {
  return {
    [QaLabelType.NO_PRODUCT]: new Set<string>(),
    [QaLabelType.PRODUCT]: new Set<string>(),
    [QaLabelType.PRIMER_DIMER]: new Set<string>(),
    [QaLabelType.UNSPECIFIC]: new Set<string>(),
    [QaLabelType.NONSENSE]: new Set<string>(),
    [QaLabelType.ISSUE]: new Set<string>(),
    [QaLabelType.OTHER]: new Set<string>(),
  };
}

function createQaLabelTmMap(): Partial<Record<QaLabelType, string>> {
  return {
    [QaLabelType.PRODUCT]: '',
    [QaLabelType.PRIMER_DIMER]: '',
    [QaLabelType.UNSPECIFIC]: '',
  };
}

function createQaLabelTmSetMap(): Record<QaLabelType, Set<string>> {
  return {
    [QaLabelType.NO_PRODUCT]: new Set<string>(),
    [QaLabelType.PRODUCT]: new Set<string>(),
    [QaLabelType.PRIMER_DIMER]: new Set<string>(),
    [QaLabelType.UNSPECIFIC]: new Set<string>(),
    [QaLabelType.NONSENSE]: new Set<string>(),
    [QaLabelType.ISSUE]: new Set<string>(),
    [QaLabelType.OTHER]: new Set<string>(),
  };
}

function createQaLabelDirtyMap(): Record<QaLabelType, boolean> {
  return {
    [QaLabelType.NO_PRODUCT]: false,
    [QaLabelType.PRODUCT]: false,
    [QaLabelType.PRIMER_DIMER]: false,
    [QaLabelType.UNSPECIFIC]: false,
    [QaLabelType.NONSENSE]: false,
    [QaLabelType.ISSUE]: false,
    [QaLabelType.OTHER]: false,
  };
}

function getQaLabelerTooltipLabel(labelerFirstNames: Array<string | null | undefined>): string | null {
  const uniqueNames = Array.from(
    new Set(
      labelerFirstNames.flatMap(name => {
        const trimmedName = name?.trim();
        return trimmedName ? [trimmedName] : [];
      }),
    ),
  );

  if (uniqueNames.length === 0) {
    return null;
  }

  if (uniqueNames.length <= 2) {
    return `By ${uniqueNames.join(', ')}`;
  }

  return `By ${uniqueNames.slice(0, 2).join(', ')} +${uniqueNames.length - 2}`;
}

function mergeQaLabels(currentLabels: QaLabel[], nextLabels: QaLabel[]): QaLabel[] {
  const nextLabelIds = new Set(nextLabels.map(label => label.id));
  return [...currentLabels.filter(label => !nextLabelIds.has(label.id)), ...nextLabels];
}

function updateChipQaLabelsCache(
  queryClient: ReturnType<typeof useQueryClient>,
  chipId: number,
  updateLabels: (currentLabels: QaLabel[]) => QaLabel[],
) {
  queryClient.setQueryData<ChipDebugResponse | undefined>([`/chip/${chipId}`], currentData => {
    if (currentData == null) {
      return currentData;
    }

    return {
      ...currentData,
      qaLabels: updateLabels(currentData.qaLabels),
    };
  });
}
